import React, { useState } from 'react'
import './DetailsCard.css';
import { formatLabel } from '../../../utils/stringUtils';
import { Chip } from '@mui/material';
import AlertLabel from '../../common/alertLabel/AlertLabel';
import ComparisonDialog from '../../common/dialog/comparisonDialog/ComparisonDialog';
import {
    compareThreeSources,
    getFieldComparisons,
    isComparableField,
    getDisplayFieldName,
    normalizeFieldName
} from '../../../utils/comparisonUtils';

const DetailsCard = ({
    name,
    cardTitle,
    detailsData,
    sorError,
    ocrData = null,
    comparisonData = null,
    comparisonData2 = null,
    source1Name = "OCR",
    source2Name = "Barcode",
    source3Name = "SOR/MV",
    enableComparison = false,
    enableThreeWayComparison = false
}) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedField, setSelectedField] = useState(null);

    const isSorRecordFound = detailsData?.isValid === 'Y' || sorError?.isValid === 'Y';

    // Determine which comparison mode to use based on available data
    const useThreeWay = enableThreeWayComparison && !!comparisonData && !!comparisonData2;
    const useTwoWay = !useThreeWay && !!comparisonData;
    const useComparison = useThreeWay || useTwoWay;

    // Debug logs to trace comparison decision
    console.log('[DetailsCard] useThreeWay:', useThreeWay, 'useTwoWay:', useTwoWay, 'useComparison:', useComparison);
    console.log('[DetailsCard] ocrData present: ', !!ocrData, 'comparisonData present:', !!comparisonData, 'comparisonData2 present:', !!comparisonData2);

    // Get field comparisons based on number of sources
    let fieldComparisons = {};

    if (useThreeWay) {
        // Three-way comparison (OCR, Barcode, SOR)
        fieldComparisons = compareThreeSources(ocrData, comparisonData, comparisonData2);
    } else if (useTwoWay) {
        // Two-way comparison (OCR, Barcode)
        fieldComparisons = getFieldComparisons(ocrData, comparisonData);
    }

    // Debug print of computed comparisons
    console.log('[DetailsCard] fieldComparisons:', fieldComparisons);

    // Build list of mismatched rows from computed comparisons
    const buildAllMismatches = () => {
        const rows = [];
        Object.entries(fieldComparisons).forEach(([normalizedField, cmp]) => {
            if (useThreeWay) {
                // require at least two sources present to consider comparison
                const hasAny = !!(cmp.normalizedOcr || cmp.normalizedBarcode || cmp.normalizedSor);
                if (!hasAny) return;
                if (!cmp.allMatch) {
                    rows.push({
                        attribute: getDisplayFieldName(normalizedField),
                        ocr: cmp.ocrValue ?? '-',
                        mrz: cmp.barcodeValue ?? '-',
                        sor: cmp.sorValue ?? '-'
                    });
                }
            } else {
                // two-way: ensure both sides exist and not match
                if (cmp.source1Value && cmp.source2Value && !cmp.matches) {
                    rows.push({
                        attribute: getDisplayFieldName(normalizedField),
                        ocr: cmp.source1Value ?? '-',
                        mrz: cmp.source2Value ?? '-',
                        sor: '-'
                    });
                }
            }
        });
        return rows;
    };

    const handleFieldClick = (fieldName, comparison) => {
        console.log('[DetailsCard] handleFieldClick called for', fieldName, 'comparison:', comparison);
        if (!comparison || !isFieldClickable(fieldName)) return;

        // When clicked, open dialog that shows ALL mismatched fields (not just clicked one)
        const mismatches = buildAllMismatches();

        if (mismatches.length === 0) {
            // safety: if none found (shouldn't happen) open dialog for single field
            if (useThreeWay) {
                setSelectedField({
                    fieldName: getDisplayFieldName(fieldName),
                    mismatches: [{
                        attribute: getDisplayFieldName(fieldName),
                        ocr: comparison.ocrValue ?? '-',
                        mrz: comparison.barcodeValue ?? '-',
                        sor: comparison.sorValue ?? '-'
                    }],
                    isThreeWay: true
                });
            } else {
                setSelectedField({
                    fieldName: getDisplayFieldName(fieldName),
                    mismatches: [{
                        attribute: getDisplayFieldName(fieldName),
                        ocr: comparison.source1Value ?? '-',
                        mrz: comparison.source2Value ?? '-',
                        sor: '-'
                    }],
                    isThreeWay: false
                });
            }
        } else {
            setSelectedField({
                fieldName: getDisplayFieldName(fieldName),
                mismatches,
                isThreeWay: useThreeWay
            });
        }
        setDialogOpen(true);
    };

    const getFieldClassName = (fieldName) => {
        const normalizedField = normalizeFieldName(fieldName);
        const comparison = fieldComparisons[normalizedField];

        if (!useComparison || !comparison) {
            return 'document-item';
        }

        // Ensure we only mark as mismatch if it is actually clickable (i.e. real mismatch with comparable values)
        const clickable = isFieldClickable(fieldName);
        if (!clickable) {
            return 'document-item';
        }

        // For three-way comparison, check if all match
        if (useThreeWay) {
            return `document-item ${comparison.allMatch ? 'field-match' : 'field-mismatch'}`;
        }

        // For two-way comparison
        return `document-item ${comparison.matches ? 'field-match' : 'field-mismatch'}`;
    };

    const isFieldClickable = (fieldName) => {
        const normalizedField = normalizeFieldName(fieldName);
        const comparison = fieldComparisons[normalizedField];

        if (!comparison) return false;

        if (useThreeWay) {
            // For three-way comparison, show if any values differ (but require at least 2 sources present)
            const hasAny = !!(comparison.normalizedOcr || comparison.normalizedBarcode || comparison.normalizedSor);
            return hasAny && !comparison.allMatch;
        }

        // For two-way comparison
        return comparison.source1Value &&
            comparison.source2Value &&
            !comparison.matches;
    };

    const renderKeyValuePairs = (data) => (
        Object.entries(data).map(([key, value]) => {
            if (key === 'portrait' ||
                !value ||
                value === "null" ||
                value === 'N/A' ||
                value === 'undefined' ||
                (typeof value === "string" && value.trim() === "")) {
                return null;
            }

            const normalizedField = normalizeFieldName(key);
            const comparison = fieldComparisons[normalizedField];
            const className = getFieldClassName(key);
            const isClickable = isFieldClickable(key);

            return (
                <div
                    className={className}
                    key={key}
                    onClick={() => isClickable && handleFieldClick(normalizedField, comparison)}
                    style={{ cursor: isClickable ? 'pointer' : 'default' }}
                >
                    <span className="info-label" title={formatLabel(key)}>
                        {formatLabel(key)}

                        {/* small info icon for mismatched clickable fields */}
                        {isClickable && (
                            <span className="info-icon" title="View comparison">
                                {/* simple inline SVG "i" icon */}
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                    <circle cx="12" cy="12" r="10" fill="#FFE7C4" />
                                    <path d="M11 10h2v7h-2zM11 7h2v2h-2z" fill="#B57D00" />
                                </svg>
                            </span>
                        )}
                    </span>

                    <span className="document-value" title={value}>{value}</span>
                </div>
            )
        })
    );

    return (
        <div className="document-info review-component-container full-width">
            <div className='document-info-header'>
                <h3 className="document-title">{cardTitle}</h3>
                {(name === "sorInfo" &&
                    (detailsData != null || sorError?.isValid != null)) &&
                    <span className='sub-title'>
                        <Chip
                            size='small' label={isSorRecordFound ? 'Record Found' : 'Record not found'}
                            sx={{
                                padding: '4px',
                                borderRadius: '4px',
                                color: '#FFFFFF',
                                backgroundColor: isSorRecordFound ? '#659330' : '#FF9500'
                            }} />
                    </span>
                }
            </div>
            <div className="document-grid">
                {(name === "documentInfo" || name === "identityInfo")
                    && renderKeyValuePairs(detailsData)}

                {(name === "sorInfo") &&
                    (
                        (sorError ? (
                            <div className="sor-grid-container">
                                {sorError?.isValid != null && sorError?.accessCode != null && (
                                    renderKeyValuePairs({
                                        isValid: sorError?.isValid,
                                        accessCode: sorError?.accessCode
                                    })
                                )}
                                <div className="alert-label-container">
                                    <AlertLabel message={sorError?.message} type="error" />
                                </div>
                            </div>

                        ) : (
                            detailsData != null ? renderKeyValuePairs(detailsData)
                                : <AlertLabel message={"Not Applicable"} type="error" />
                        ))
                    )
                }
            </div>

            {/* Comparison Dialog */}
            <ComparisonDialog
                isOpen={dialogOpen}
                onClose={() => setDialogOpen(false)}
                // single-field fallback props kept for compatibility
                fieldName={selectedField?.fieldName}
                mismatches={selectedField?.mismatches}
                source1Data={selectedField?.source1Value}
                source2Data={selectedField?.source2Value}
                source3Data={selectedField?.source3Value}
                source1Name={source1Name}
                source2Name={source2Name}
                source3Name={source3Name}
                showThreeSources={selectedField?.isThreeWay}
            />
        </div>
    )
}

export default DetailsCard